import React, { useEffect, useMemo, useState } from 'react';
import SwitchWithLabel from 'components/SwitchWithLabel/SwitchWithLabel';
import useScopedTranslation from 'hooks/useScopedTranslation';
import { contractGenI18n } from '../i18n';
import './steps.css';
import ConfirmModal from 'components/ConfirmModal/ConfirmModal';
import NewCaseModal from '../../LegalCases/NewCaseModal';
import {
  fetchCasesByClient,
  fetchServicesByBusinessLine,
  searchCreditors,
} from '../../../services/contractGeneratorService';
import { getCreateInfo } from '../../../services/caseServices';
import { getAllCourts } from '../../../services/courtServices';
import { getSponsors, getUsersPaged } from '../../../services/usersServices';
import { getCurrentUser } from '../../../services/authService';

const StepCase = ({ DesignSystemComponents, formData, onChange, catalogs }) => {
  const { ComboBox, Input, Button, Table, IconButton, Checkbox } =
    DesignSystemComponents;
  const { t } = useScopedTranslation(contractGenI18n);

  const [confirmContinueEmpty] = useState(false);
  const [showCaseModal, setShowCaseModal] = useState(false);
  const [caseOptions, setCaseOptions] = useState([]);
  const [serviceOptions, setServiceOptions] = useState([]);
  const [creditorOptions, setCreditorOptions] = useState([]);
  const [creditorSearch, setCreditorSearch] = useState('');
  const [lawyers, setLawyers] = useState([]);
  const [sponsors, setSponsors] = useState([]);
  const [courts, setCourts] = useState([]);
  const [createInfo, setCreateInfo] = useState(null);
  const [modalClients, setModalClients] = useState([]);
  const [userId, setUserId] = useState(null);
  const [creditorDraft, setCreditorDraft] = useState({
    creditor: null,
    amount: '',
    details: '',
  });

  const selectedClient = formData?.cliente;
  const isActiveCase = formData?.activeCase ?? true;
  const isActiveCreditor = formData?.activeCreditor ?? true;
  const selectedCaseIds = formData?.selectedCaseIds || [];
  const creditors = formData?.creditors || [];

  const resolveNumericClientId = (client) => {
    if (!client) return null;
    const candidates = [
      client.id,
      client.clientId,
      client.backendId,
      client.idClient,
      client.value,
    ];

    for (const candidate of candidates) {
      const parsed = Number(candidate);
      if (Number.isFinite(parsed) && parsed > 0) {
        return parsed;
      }
    }

    return null;
  };

  useEffect(() => {
    if (formData?.activeCase === undefined) {
      onChange({ activeCase: true, activeCreditor: false });
    }
  }, [formData?.activeCase, onChange]);

  useEffect(() => {
    const authUser = getCurrentUser();
    if (authUser?.userId) {
      setUserId(authUser.userId);
    }
  }, []);

  useEffect(() => {
    if (!showCaseModal) return;

    const loadModalData = async () => {
      try {
        const [createInfoResp, courtsResp, sponsorsResp, usersResp] =
          await Promise.all([
            getCreateInfo(),
            getAllCourts(),
            getSponsors(),
            getUsersPaged(1, 2000, true),
          ]);

        setCreateInfo(createInfoResp || null);
        setCourts(courtsResp || []);
        setSponsors(
          (sponsorsResp || []).map((u) => ({
            label: u.name,
            value: u.id,
            ...u,
          }))
        );
        setLawyers(
          (usersResp?.users || []).map((u) => ({
            label: u.name,
            value: u.id,
            ...u,
          }))
        );
      } catch (error) {
        console.error(error);
      }
    };

    loadModalData();
  }, [showCaseModal]);

  useEffect(() => {
    if (!selectedClient) {
      setModalClients([]);
      return;
    }

    const numericClientId = resolveNumericClientId(selectedClient);

    setModalClients([
      {
        id: numericClientId,
        name: selectedClient.name || selectedClient.label,
        identifier: selectedClient.rut || selectedClient.identifier || '',
      },
    ]);
  }, [selectedClient]);

  useEffect(() => {
    const loadServices = async () => {
      if (!formData?.lineaDeNegocio?.value) {
        setServiceOptions([]);
        return;
      }
      try {
        const response = await fetchServicesByBusinessLine(
          formData.lineaDeNegocio.value
        );
        setServiceOptions(response || []);
      } catch (error) {
        console.error(error);
        setServiceOptions([]);
      }
    };
    loadServices();
  }, [formData?.lineaDeNegocio?.value]);

  useEffect(() => {
    const loadCases = async () => {
      if (!selectedClient?.rut) {
        setCaseOptions([]);
        return;
      }
      try {
        const response = await fetchCasesByClient(selectedClient.rut);
        setCaseOptions(response || []);
      } catch (error) {
        console.error(error);
        setCaseOptions([]);
      }
    };
    loadCases();
  }, [selectedClient?.rut]);

  useEffect(() => {
    if (!selectedClient?.rut) {
      if ((selectedCaseIds || []).length > 0) {
        onChange({ selectedCaseIds: [], caso: {} });
      }
      return;
    }

    if (!caseOptions.length || !(selectedCaseIds || []).length) return;

    const validIds = new Set(caseOptions.map((x) => x.caseId));
    const normalizedSelected = (selectedCaseIds || []).filter((id) =>
      validIds.has(id)
    );

    const hasChanges =
      normalizedSelected.length !== (selectedCaseIds || []).length ||
      normalizedSelected.some((id, index) => id !== selectedCaseIds[index]);

    if (hasChanges) {
      onChange({
        selectedCaseIds: normalizedSelected,
        ...(normalizedSelected.length === 0 ? { caso: {} } : {}),
      });
    }
  }, [caseOptions, selectedClient?.rut, selectedCaseIds, onChange]);

  useEffect(() => {
    const loadCreditors = async () => {
      if (!creditorSearch || creditorSearch.trim().length < 2) {
        setCreditorOptions([]);
        return;
      }
      try {
        const response = await searchCreditors({ q: creditorSearch.trim(), take: 30 });
        setCreditorOptions(
          (response || []).map((x) => ({
            label: x.name,
            value: x.id,
            creditorName: x.name,
          }))
        );
      } catch (error) {
        console.error(error);
        setCreditorOptions([]);
      }
    };

    const timeoutId = setTimeout(loadCreditors, 300);
    return () => clearTimeout(timeoutId);
  }, [creditorSearch]);

  const selectedCases = useMemo(
    () => caseOptions.filter((x) => selectedCaseIds.includes(x.caseId)),
    [caseOptions, selectedCaseIds]
  );

  const addCreditor = () => {
    if (!creditorDraft.creditor) return;
    onChange({
      creditors: [
        ...creditors,
        {
          _uid: `${Date.now()}-${Math.random()}`,
          creditorId: creditorDraft.creditor.value,
          creditorName:
            creditorDraft.creditor.creditorName || creditorDraft.creditor.label,
          amount: Number(creditorDraft.amount || 0),
          details: creditorDraft.details || null,
        },
      ],
    });
    setCreditorDraft({ creditor: null, amount: '', details: '' });
  };

  return (
    <div className='step-content mb-4'>
      <p className='body fw-semibold mb-3'>{t('cg_case_title')}</p>
      <div className='row'>
        <div className='col-3'>
          <ComboBox
            label={t('cg_case_business_line')}
            menuPlacement='bottom'
            onChange={(value) => {
              const blId = value?.value ?? value?.id ?? (Array.isArray(value) ? value[0]?.value : null);
              onChange({ lineaDeNegocio: value, servicio: null, _businessLineId: Number(blId) || 0, _serviceId: 0 });
            }}
            options={catalogs?.businessLines || []}
            placeholder={t('cg_case_bl_placeholder')}
            value={formData?.lineaDeNegocio || null}
            required
          />
        </div>
        <div className='col-3'>
          <ComboBox
            label={t('cg_case_service')}
            menuPlacement='bottom'
            onChange={(value) => {
              const svcId = value?.value ?? value?.id ?? (Array.isArray(value) ? value[0]?.value : null);
              onChange({ servicio: value, _serviceId: Number(svcId) || 0 });
            }}
            options={serviceOptions}
            placeholder={t('cg_case_service_placeholder')}
            value={formData?.servicio || null}
            required
          />
        </div>
        <div className='col-3'>
          <SwitchWithLabel
            variant='field'
            label={t('cg_case_switch_case')}
            checked={isActiveCase}
            onChange={(checked) =>
              onChange({
                activeCase: checked,
                ...(checked && { activeCreditor: false }),
              })
            }
            helperText={t('cg_case_switch_case_helper')}
          />
        </div>
        <div className='col-3'>
          <SwitchWithLabel
            variant='field'
            label={t('cg_case_switch_creditor')}
            checked={!isActiveCase && isActiveCreditor}
            onChange={(checked) =>
              onChange({
                activeCreditor: checked,
                ...(checked && { activeCase: false }),
              })
            }
            helperText={t('cg_case_switch_creditor_helper')}
            disabled={isActiveCase}
          />
        </div>
      </div>

      {!isActiveCase && isActiveCreditor && (
        <>
          <hr className='line' />
          <div className='row mt-3'>
            <div className='col-12'>
              <p className='body fw-semibold mb-3'>{t('cg_case_creditor_title')}</p>
            </div>
          </div>
          <div className='row'>
            <div className='col-3'>
              <ComboBox
                label={t('cg_case_creditor_label')}
                menuPlacement='bottom'
                onChange={(value) =>
                  setCreditorDraft((prev) => ({ ...prev, creditor: value }))
                }
                onInputChange={(text) => setCreditorSearch(text || '')}
                options={creditorOptions}
                placeholder={t('cg_case_creditor_placeholder')}
                value={creditorDraft.creditor}
                required
              />
            </div>
            <div className='col-3'>
              <Input
                label={t('cg_case_amount')}
                name='creditor-amount'
                onChange={(e) =>
                  setCreditorDraft((prev) => ({
                    ...prev,
                    amount: e.target.value,
                  }))
                }
                placeholder={t('cg_case_amount_placeholder')}
                type='number'
                value={creditorDraft.amount}
                required
              />
            </div>
            <div className='col-3'>
              <Input
                label={t('cg_case_detail')}
                name='creditor-details'
                onChange={(e) =>
                  setCreditorDraft((prev) => ({
                    ...prev,
                    details: e.target.value,
                  }))
                }
                placeholder={t('cg_case_detail_placeholder')}
                type='text'
                value={creditorDraft.details}
              />
            </div>
            <div className='col-3 d-flex align-items-center'>
              <Button label={t('cg_case_btn_add')} size='sm' variant='secondary' onClick={addCreditor} />
            </div>
          </div>
          <div className='row'>
            <div className='col-12'>
              <div className='table-container table-creditors'>
                <Table
                  columns={[
                    { accessor: 'creditorName', label: t('cg_case_table_creditor') },
                    { accessor: 'amount', label: t('cg_case_table_amount') },
                    { accessor: 'details', label: t('cg_case_table_detail') },
                    {
                      label: '',
                      render: (row) => (
                        <IconButton
                          ariaLabel={t('cg_case_remove_creditor_aria')}
                          icon='trash'
                          iconStroke='var(--error-500)'
                          size='md'
                          onClick={() =>
                            onChange({
                              creditors: creditors.filter((x) => x._uid !== row._uid),
                            })
                          }
                        />
                      ),
                    },
                  ]}
                  data={creditors}
                />
              </div>
            </div>
          </div>
        </>
      )}

      {isActiveCase && (
        <>
          <hr className='line' />
          <div className='row mt-3'>
            <div className='col-12 d-flex justify-content-between mb-3'>
              <p className='body fw-semibold'>{t('cg_case_cause_title')}</p>
              <Button
                label={t('cg_case_new_case')}
                variant='naked'
                iconStart='plus'
                onClick={() => setShowCaseModal(true)}
              />
            </div>
          </div>
          <div className='row'>
            <div className='col-6' style={{ borderRight: '1px solid var(--gray-300)' }}>
              <p className='body fw-medium'>{t('cg_case_client_cases')}</p>
              {caseOptions.length > 0 ? (
                caseOptions.map((item) => (
                  <Checkbox
                    key={item.caseId}
                    id={`case-${item.caseId}`}
                    onChange={(eventOrChecked) => {
                      const isChecked =
                        typeof eventOrChecked === 'boolean'
                          ? eventOrChecked
                          : Boolean(eventOrChecked?.target?.checked);
                      const next = isChecked
                        ? [...new Set([...selectedCaseIds, item.caseId])]
                        : selectedCaseIds.filter((id) => id !== item.caseId);
                      onChange({
                        selectedCaseIds: next,
                        caso: isChecked ? item : formData?.caso || {},
                      });
                    }}
                    checked={selectedCaseIds.includes(item.caseId)}
                    label={`${item.code || t('cg_case_no_rol')} - ${item.title || t('cg_case_no_title')} - ${item.tribunal || '--'}`}
                  />
                ))
              ) : (
                <p className='caption'>{t('cg_case_no_cases_found')}</p>
              )}
            </div>
            <div className='col-6'>
              <p className='body fw-medium'>{t('cg_case_selected_cases')}</p>
              {selectedCases.length > 0 ? (
                <ol className='selected-cases'>
                  {selectedCases.map((item) => (
                    <li key={item.caseId}>
                      {item.code || t('cg_case_no_rol')} - {item.title || t('cg_case_no_title')}
                    </li>
                  ))}
                </ol>
              ) : (
                <p className='caption'>{t('cg_case_no_cases_selected')}</p>
              )}
            </div>
          </div>
        </>
      )}

      <ConfirmModal
        Modal={DesignSystemComponents.Modal}
        show={confirmContinueEmpty}
        title={t('cg_case_confirm_title')}
        message={t('cg_case_confirm_message')}
        confirmLabel={t('cg_case_confirm_label')}
        confirmVariant='warning'
        onConfirm={() => {}}
        onClose={() => {}}
      />

      <NewCaseModal
        DesignSystemComponents={DesignSystemComponents}
        show={showCaseModal}
        onClose={() => setShowCaseModal(false)}
        clients={modalClients}
        lawyers={lawyers}
        courts={courts}
        createInfo={createInfo}
        userId={userId}
        sponsors={sponsors}
        fromMonitoring={false}
        preselectedClient={
          selectedClient
            ? {
                label: `${selectedClient.name || selectedClient.label} (${selectedClient.rut || selectedClient.identifier || ''})`,
                value: resolveNumericClientId(selectedClient),
                identifier: selectedClient.rut || selectedClient.identifier || '',
              }
            : null
        }
        onAfterCreate={async () => {
          if (!selectedClient?.rut) return;
          try {
            const response = await fetchCasesByClient(selectedClient.rut);
            setCaseOptions(response || []);
          } catch (error) {
            console.error(error);
          }
        }}
      />
    </div>
  );
};

export default StepCase;
